import { useEffect, useMemo } from 'react';
import { Button } from '@wayste/sour-ui';
import posthog from 'posthog-js';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

// The browser caches a failed dynamic import for the life of the document, so only a full reload fetches the chunk again.
const CHUNK_LOAD_ERROR_PATTERN =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|is not a valid JavaScript MIME type/i;
const CHUNK_RELOAD_STORAGE_KEY = 'route-chunk-reload-at';
// Covers a slow reload, so a chunk that is still missing after the reload shows the error screen instead of reloading again.
const CHUNK_RELOAD_WINDOW_MS = 60_000;

export default function ErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();

    // Decided once per error, because the effect below writes the marker this reads.
    const shouldReload = useMemo(() => {
        if (!(error instanceof Error) || !CHUNK_LOAD_ERROR_PATTERN.test(error.message)) {
            return false;
        }
        try {
            return Date.now() - Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? 0) >= CHUNK_RELOAD_WINDOW_MS;
        } catch {
            // Without storage there is no guard against a reload loop, so the error screen shows instead.
            return false;
        }
    }, [error]);

    useEffect(() => {
        if (shouldReload) {
            sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
            window.location.reload();
            return;
        }

        console.error(error);
        if (!isRouteErrorResponse(error)) {
            posthog.captureException(error, { route: window.location.pathname });
        }
    }, [error, shouldReload]);

    if (shouldReload) {
        return null;
    }

    if (isRouteErrorResponse(error)) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-4">
                <h1 className="text-4xl font-bold">Oops!</h1>
                <p className="text-xl">Sorry, an unexpected error has occurred.</p>
                <p className="text-gray-500">
                    {error.status} - {error.statusText}
                </p>
                <Button className="btn-primary px-5 py-2.5" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col items-center justify-center gap-4">
            <h1 className="text-4xl font-bold">Oops!</h1>
            <p className="text-xl">Sorry, an unexpected error has occurred.</p>
            <Button className="btn-primary px-5 py-2.5" onClick={() => navigate(-1)}>
                Go Back
            </Button>
        </div>
    );
}
